import React, { useEffect, useRef, useState } from 'react';
import { styles } from './styles';
import SignatureMark from './signatureMark';
import ReflectionButton from '../reflectionButton/reflectionButton';
import { COLORS, RIPPLE_ON_DARK, RIPPLE_ON_LIGHT } from '../../globalStyles';
import { askVae, VaeError, VaeTurn } from '../../vae/client';
import { VAE_CONFIGURED } from '../../vae/config';

type Msg = { role: 'user' | 'assistant'; content: string; note?: boolean };

const GREETING: Msg = {
  role: 'assistant',
  content: "Hi, I'm VAE, Novaeon's AI assistant. I'm here to answer any questions you might have about our work, our products, or how to start a project.",
};

const NOT_CONFIGURED: Msg = {
  role: 'assistant',
  content: "VAE is currently offline. In the meantime, feel free to reach us at hello@novaeon.io.",
  note: true,
};

/**
 * VAE — Novaeon's site assistant. A floating launcher (the diamond mark as its
 * avatar) opens a chat panel that talks to the vaeChat Cloud Function. Answers
 * are constrained server-side to Novaeon topics; see /vae-backend.
 */
const Vae: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([VAE_CONFIGURED ? GREETING : NOT_CONFIGURED]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Blinking "thinking" caret — VAE's one motion moment. Reduced-motion safe.
  useEffect(() => {
    const id = 'vae-style';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = `
      .vae-caret { animation: vae-blink 1.05s steps(1) infinite; }
      @keyframes vae-blink { 0%, 50% { opacity: 1 } 50.01%, 100% { opacity: 0 } }
      @media (prefers-reduced-motion: reduce) { .vae-caret { animation: none } }
    `;
    document.head.appendChild(el);
  }, []);

  // Keep the newest message in view.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, pending, open]);

  // Focus the composer when the panel opens; close on Escape. Skip the autofocus
  // on touch devices so opening VAE doesn't immediately pop the on-screen keyboard.
  useEffect(() => {
    if (!open) return;
    if (!window.matchMedia('(pointer: coarse)').matches) {
      inputRef.current?.focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const grow = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 110)}px`;
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || pending || !VAE_CONFIGURED) return;

    const next = [...messages, { role: 'user' as const, content: text }];
    setMessages(next);
    setDraft('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setPending(true);

    // Only the real conversation turns go to the model (drop notes/greeting).
    const history: VaeTurn[] = next
      .filter((m) => !m.note)
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const reply = await askVae(history);
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      const message =
        e instanceof VaeError ? e.message : 'Something went wrong. Please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content: message, note: true }]);
    } finally {
      setPending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  if (!open) {
    return (
      <div style={styles.launcherWrap}>
        <ReflectionButton
          style={styles.launcher}
          highlightColor={RIPPLE_ON_DARK}
          aria-label="Open VAE, Novaeon's assistant"
          onClick={() => setOpen(true)}
        >
          <span style={styles.launcherStack}>
            <span style={styles.launcherLabel}>Ask</span>
            <SignatureMark size={48} title="VAE" color={COLORS.text} />
            <span style={styles.launcherLabel}>VAE</span>
          </span>
        </ReflectionButton>
      </div>
    );
  }

  return (
    <section className="vae-panel" style={styles.panel} aria-label="VAE chat">
      <div style={styles.header}>
        <SignatureMark size={36} title="VAE" color={COLORS.text} />
        <div style={styles.headerText}>
          <span style={styles.headerName}>VAE</span>
          <span style={styles.headerSub}>Novaeon assistant</span>
        </div>
        <button style={styles.close} aria-label="Close VAE" onClick={() => setOpen(false)}>
          <svg
            viewBox="0 0 24 24"
            width="30"
            height="30"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <div ref={listRef} style={styles.messages}>
        {messages.map((m, i) => {
          const speaker = m.note ? 'Note' : m.role === 'user' ? 'You' : 'VAE';
          const eyebrowStyle = m.note
            ? styles.eyebrowNote
            : m.role === 'user'
              ? styles.eyebrowUser
              : styles.eyebrowVae;
          return (
            <div key={i} style={styles.turn}>
              <span style={{ ...styles.eyebrow, ...eyebrowStyle }}>{speaker}</span>
              <span className="vae-body" style={{ ...styles.body, ...(m.note ? styles.bodyNote : {}) }}>{m.content}</span>
            </div>
          );
        })}

        {pending && (
          <div style={styles.turn} aria-live="polite" aria-label="VAE is thinking">
            <span style={{ ...styles.eyebrow, ...styles.eyebrowVae }}>VAE</span>
            <span>
              <span className="vae-caret" style={styles.caret} />
            </span>
          </div>
        )}
      </div>

      <div style={styles.form}>
        <textarea
          ref={inputRef}
          style={styles.input}
          rows={1}
          value={draft}
          placeholder={VAE_CONFIGURED ? 'Ask about Novaeon…' : 'VAE isn’t connected yet'}
          disabled={!VAE_CONFIGURED || pending}
          onChange={(e) => {
            setDraft(e.target.value);
            grow(e.target);
          }}
          onKeyDown={onKeyDown}
        />
        <ReflectionButton
          style={{ ...styles.send, opacity: !VAE_CONFIGURED || pending || !draft.trim() ? 0.5 : 1 }}
          highlightColor={RIPPLE_ON_LIGHT}
          disabled={!VAE_CONFIGURED || pending || !draft.trim()}
          aria-label="Send message"
          onClick={() => void send()}
        >
          →
        </ReflectionButton>
      </div>
    </section>
  );
};

export default Vae;
